/**
 * KIAN-Sprache: Zuhören (Aufnahme → Text) und Vorlesen (Text → Audio).
 *
 * Aufnahme und Audio laufen direkt über fetch statt apiRequest: der setzt JSON als Content-Type
 * und liest JSON zurück. Deshalb den CSRF-Header selbst (CLAUDE.md, „CSRF — beide Portale").
 */
import { API_BASE, ApiError, type RequestFn } from '../http';
import { withCsrfHeader } from '../../csrf';
import type { ChatMessage } from './assistant';

export interface KianVoiceClientConfig {
  available: boolean;
  reason: string | null;
  sttModel: string;
  ttsModel: string;
  ttsVoice: string;
  ttsUsdPerMillionChars: number | null;
}

export interface KianVoiceSettings {
  enabled: boolean;
  sttModel: string;
  ttsModel: string;
  ttsVoice: string;
  ttsSpeed: number;
  ttsUsdPerMillionChars: number | null;
}

export interface KianVoiceSettingsResponse {
  config: KianVoiceSettings;
  available: boolean;
  reason: string | null;
  connectionHost?: string | null;
}

export interface KianTranscription {
  text: string;
  model: string;
  seconds: number | null;
  costEur: number | null;
}

export interface KianSpeechAudio {
  audio: ArrayBuffer;
  model: string | null;
  chars: number;
  costEur: number | null;
}

async function failure(res: Response): Promise<ApiError> {
  let message = `HTTP ${res.status}`;
  try {
    const body = (await res.json()) as { error?: string };
    if (body?.error) message = body.error;
  } catch {
    /* kein JSON (z. B. Fehlerseite eines Proxys) */
  }
  return new ApiError(message, message, undefined, undefined, res.status);
}

async function readAudio(res: Response): Promise<KianSpeechAudio> {
  if (!res.ok) throw await failure(res);
  const cost = res.headers.get('X-Kian-Voice-Cost-Eur');
  return {
    audio: await res.arrayBuffer(),
    model: res.headers.get('X-Kian-Voice-Model'),
    chars: Number(res.headers.get('X-Kian-Voice-Chars') ?? 0) || 0,
    costEur: cost != null && cost !== '' ? Number(cost) : null,
  };
}

export async function transcribeKianVoice(audio: Blob, signal?: AbortSignal): Promise<KianTranscription> {
  const res = await fetch(`${API_BASE}/kian/voice/transcribe`, {
    method: 'POST',
    headers: withCsrfHeader('POST', { 'Content-Type': audio.type || 'audio/wav' }),
    body: audio,
    credentials: 'include',
    signal,
  });
  if (!res.ok) throw await failure(res);
  return (await res.json()) as KianTranscription;
}

export async function speakKianVoice(text: string, signal?: AbortSignal): Promise<KianSpeechAudio> {
  const res = await fetch(`${API_BASE}/kian/voice/speak`, {
    method: 'POST',
    headers: withCsrfHeader('POST', { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ text }),
    credentials: 'include',
    signal,
  });
  return readAudio(res);
}

export async function previewKianVoice(body: {
  text?: string;
  ttsModel?: string;
  ttsVoice?: string;
  ttsSpeed?: number;
}): Promise<KianSpeechAudio> {
  const res = await fetch(`${API_BASE}/settings/kian-voice/preview`, {
    method: 'POST',
    headers: withCsrfHeader('POST', { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
    credentials: 'include',
  });
  return readAudio(res);
}

export type KianVoiceTurnKind = 'confirm' | 'reject' | 'outcome' | 'repeat-yes' | 'goodbye';

export interface KianVoiceTurnResult {
  /** Vom Server gespeicherte Nachrichten — gehören so in den Chat */
  messages: ChatMessage[];
  /** Vorzulesen (steht als letzte KIAN-Nachricht in `messages`) */
  speak: string | null;
  executed?: boolean;
  pending?: boolean;
}

export function createKianVoiceApi(request: RequestFn) {
  return {
    kianVoiceTurn(body: { sessionId: string; kind: KianVoiceTurnKind; actionId?: string | null; said?: string | null }) {
      // Verbrauch zeigt der Gesprächsmodus selbst an — kein zusätzlicher KIAN-Hinweis
      return request<KianVoiceTurnResult>('/kian/voice/turn', {
        method: 'POST',
        body: JSON.stringify(body),
        kianUsageInline: true,
      });
    },
    getKianVoiceConfig() {
      return request<KianVoiceClientConfig>('/kian/voice/config');
    },
    getKianVoiceSettings() {
      return request<KianVoiceSettingsResponse>('/settings/kian-voice');
    },
    saveKianVoiceSettings(patch: Partial<KianVoiceSettings>) {
      return request<KianVoiceSettingsResponse>('/settings/kian-voice', { method: 'PUT', body: JSON.stringify(patch) });
    },
  };
}
